import { useEffect, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { readToken } from '../../theme/tokens';
import { TAU, fitCanvas, observeCanvasResize } from './canvas';
import { WORLD_MAP_COLUMNS, WORLD_MAP_ROWS, worldMapDots } from './WorldMap';

/**
 * The page background: a radial glow with a dotted world map floating in it.
 *
 * A port of `AppBackground` in `ui/components/AppBackground.kt`. Like the Kotlin
 * one it **wraps a screen's content** rather than being something a screen has
 * to remember to apply, so every screen gets the same treatment and none of them
 * has to know how it is drawn.
 *
 * The work is split across two technologies, which Compose does not have to do:
 *
 * - **The glow is CSS** (`.app-bg` in components.css), a `radial-gradient` with
 *   Decor.pageGlow's exact centre bias, radius factor and stops. A gradient the
 *   browser paints itself re-resolves on every viewport change for free —
 *   including the ones that have no resize event worth acting on, like iOS
 *   collapsing the URL bar mid-scroll. Drawing it on the canvas instead would
 *   mean repainting several hundred thousand pixels for each of those.
 * - **The dots are a canvas.** They depend only on the size, so like Compose's
 *   `drawWithCache` they are resolved once per size change and not per frame.
 *   The alternative — one absolutely-positioned element per land cell — is ~700
 *   DOM nodes that the layout engine walks on every resize, for something that
 *   is pure texture.
 *
 * The element is `position: fixed`, so the glow lights the *screen* rather than
 * the document; a gradient that scrolls away leaves the bottom of a long
 * Settings page flat.
 */
export function AppBackground({ children }: { children: ReactNode }): JSX.Element {
  const canvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;

    paint(element);
    return observeCanvasResize(element, () => paint(element));
  }, []);

  return (
    // `.app-shell` is the full-height column every screen lays itself out in,
    // and it exists here rather than in each screen for the same reason the
    // Kotlin version is a wrapper: it establishes the stacking context that puts
    // the `z-index: -1` background behind the content and in front of nothing
    // else.
    <div className="app-shell">
      <div className="app-bg" aria-hidden="true">
        <canvas className="app-bg__map" ref={canvas} />
      </div>
      {children}
    </div>
  );
}

/**
 * Repaints the dotted map at the canvas's current size.
 *
 * Every fraction below is `AppBackground.kt`'s, unchanged — the map is wider
 * than the viewport and biased above centre on purpose, so the visible part is
 * the populated middle of the world rather than a whole globe shrunk to fit.
 *
 * Measuring inside the paint is right here and would not be in `GlobeHero`:
 * this runs once per size change, so the layout read it costs is a read per
 * resize, which is exactly when the answer changes.
 */
function paint(canvas: HTMLCanvasElement): void {
  const context = canvas.getContext('2d');
  // No 2D context is survivable: the CSS glow is a separate element and still
  // renders, so the screen loses its texture and nothing else. Decoration is
  // never worth taking a field device's dashboard down for.
  if (!context) return;

  const box = fitCanvas(canvas, context);
  if (!box) return;
  const { width, height } = box;

  const mapWidth = width * MAP_WIDTH_FRACTION;
  const mapHeight = (mapWidth * WORLD_MAP_ROWS.length) / WORLD_MAP_COLUMNS;
  const left = (width - mapWidth) / 2;
  const top = (height - mapHeight) * MAP_VERTICAL_BIAS;
  // The floor is what keeps the map visible on a small viewport: a dot rounded
  // below one device pixel is antialiased down to nothing and the continents
  // simply vanish rather than getting fainter.
  const dotRadius = Math.max(Math.min(width, height) * DOT_RADIUS_FRACTION, MIN_DOT_RADIUS_PX);

  // Read from tokens.css rather than repeated here as a literal: a canvas cannot
  // resolve `var()`, and a hard-coded `#00f0ff` in a component is exactly the
  // second source of truth tokens.css warns about.
  context.fillStyle = readToken('--neon-cyan', '#00f0ff');
  // Applied as a global alpha instead of baked into the colour so the token can
  // stay the plain hex it is in tokens.css.
  context.globalAlpha = MAP_ALPHA;

  for (const dot of worldMapDots()) {
    context.beginPath();
    context.arc(left + dot.x * mapWidth, top + dot.y * mapHeight, dotRadius, 0, TAU);
    context.fill();
  }
}

/**
 * Kept low on purpose (Android's `MAP_ALPHA`). The map is texture, not
 * information: at any opacity where you can actually read the continents it
 * starts competing with the numbers, which are the reason the screen exists.
 */
const MAP_ALPHA = 0.05;
const MAP_WIDTH_FRACTION = 1.35;
const MAP_VERTICAL_BIAS = 0.32;
const DOT_RADIUS_FRACTION = 0.0022;

/** Android's `MIN_DOT_RADIUS_PX = 0.6.dp.value`; 1dp and 1 CSS px are the same unit here. */
const MIN_DOT_RADIUS_PX = 0.6;
